/*
  Landing PDP components (product.landing template)
  - lp-carousel: scroll-snap photo carousel with arrows + thumbnail sync
  - lp-bundle: quantity-break offer (tier select -> price math -> order summary -> native ATC)
  - lp-sticky-cta: floating CTA that appears after the hero and hides while the offer is on screen
  All state is kept inside each component; nothing global.
*/

if (!customElements.get('lp-carousel')) {
  customElements.define(
    'lp-carousel',
    class LpCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-lp-track]');
        this.slides = Array.from(this.querySelectorAll('[data-lp-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-lp-thumb]'));
        this._onClick = this._onClick.bind(this);
        this.addEventListener('click', this._onClick);

        // Highlight the thumbnail of the slide currently snapped into view
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio >= 0.6) {
                const index = this.slides.indexOf(entry.target);
                this._setActive(index);
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this._observer.observe(slide));
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this._observer?.disconnect();
      }

      _onClick(event) {
        const arrow = event.target.closest('[data-lp-arrow]');
        if (arrow) {
          const direction = arrow.dataset.lpArrow === 'next' ? 1 : -1;
          this._goTo(this._activeIndex() + direction);
          return;
        }
        const thumb = event.target.closest('[data-lp-thumb]');
        if (thumb) this._goTo(this.thumbs.indexOf(thumb));
      }

      _activeIndex() {
        const active = this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
        return active === -1 ? 0 : active;
      }

      _goTo(index) {
        const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
        this.slides[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        this._setActive(clamped);
      }

      _setActive(index) {
        this.thumbs.forEach((thumb, i) => {
          if (i === index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
      }
    }
  );
}

if (!customElements.get('lp-bundle')) {
  customElements.define(
    'lp-bundle',
    class LpBundle extends HTMLElement {
      connectedCallback() {
        try {
          this.variants = JSON.parse(this.querySelector('[data-lp-variants]')?.textContent || '[]');
        } catch (error) {
          this.variants = [];
        }
        this.moneyFormat = this.dataset.currencySymbol || '$';
        this.price = Number(this.dataset.price); // cents
        this.compareAt = Number(this.dataset.compareAt) || this.price;
        this._onChange = this._onChange.bind(this);
        this.addEventListener('change', this._onChange);
        this._syncVariant();
        this._syncTier();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onChange);
      }

      _onChange(event) {
        if (event.target.matches('[data-lp-option]')) this._syncVariant();
        if (event.target.matches('[data-lp-tier-input]')) this._syncTier();
      }

      _selectedOptions() {
        const color = this.querySelector('[data-lp-option="color"]:checked')?.value;
        const size = this.querySelector('[data-lp-option="size"]:checked')?.value;
        return { color, size };
      }

      _syncVariant() {
        const { color, size } = this._selectedOptions();
        const variant = this.variants.find((v) => v.color === color && v.size === size);
        const idInput = this.querySelector('input[name="id"]');
        const warning = this.querySelector('[data-lp-unavailable]');
        if (variant && idInput) idInput.value = variant.id;
        if (warning) warning.hidden = !variant || variant.available;
        const button = this.querySelector('[ref="addToCartButton"]');
        if (button) button.disabled = !variant || !variant.available;
      }

      _syncTier() {
        const tier = this.querySelector('[data-lp-tier-input]:checked')?.closest('[data-lp-tier]');
        if (!tier) return;
        const quantity = parseInt(tier.dataset.qty, 10) || 1;
        const extraPercent = parseFloat(tier.dataset.extraPercent) || 0;

        this.querySelectorAll('[data-lp-tier]').forEach((card) => {
          card.dataset.selected = String(card === tier);
        });

        const productForm = this.querySelector('product-form-component');
        if (productForm) productForm.dataset.quantityDefault = String(quantity);
        const quantityInput = this.querySelector('[data-lp-quantity]');
        if (quantityInput) quantityInput.value = String(quantity);

        // Shopify floors the per-unit discount amount — mirror that exactly
        const unit = this.price - Math.floor((this.price * extraPercent) / 100);
        const subtotalFull = this.compareAt * quantity;
        const total = unit * quantity;
        const saved = subtotalFull - total;

        this._setText('[data-lp-summary-qty]', `${quantity} × InvisiLift Strapless Bra`);
        this._setText('[data-lp-summary-compare]', this._money(this.compareAt) + ' each');
        this._setText('[data-lp-summary-unit]', this._money(unit) + ' each');
        this._setText('[data-lp-summary-save]', `You Save ${Math.round((saved / subtotalFull) * 100)}%`);
        this._setText('[data-lp-summary-total]', this._money(total));
        const shippingRow = this.querySelector('[data-lp-summary-shipping]');
        if (shippingRow) {
          const freeShippingMin = Number(this.dataset.freeShippingMin) || 0;
          shippingRow.dataset.free = String(freeShippingMin === 0 || total >= freeShippingMin);
        }
      }

      _setText(selector, text) {
        const node = this.querySelector(selector);
        if (node) node.textContent = text;
      }

      _money(cents) {
        return this.moneyFormat + (cents / 100).toFixed(2);
      }
    }
  );
}

if (!customElements.get('lp-sticky-cta')) {
  customElements.define(
    'lp-sticky-cta',
    class LpStickyCta extends HTMLElement {
      connectedCallback() {
        this._hero = document.querySelector('[data-lp-hero]');
        this._offer = document.getElementById('lp-offer');
        this._onScroll = this._onScroll.bind(this);
        this._onClick = this._onClick.bind(this);
        this.addEventListener('click', this._onClick);
        // Horizon scrolls an inner container, not the window — capture phase catches both
        window.addEventListener('scroll', this._onScroll, { passive: true, capture: true });
        this._onScroll();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        window.removeEventListener('scroll', this._onScroll, { capture: true });
      }

      _onClick(event) {
        if (!event.target.closest('[data-lp-sticky-button]')) return;
        event.preventDefault();
        this._offer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      _onScroll() {
        const pastHero = this._hero
          ? this._hero.getBoundingClientRect().bottom < 0
          : window.scrollY > 600;
        let offerVisible = false;
        if (this._offer) {
          const rect = this._offer.getBoundingClientRect();
          offerVisible = rect.top < window.innerHeight && rect.bottom > 0;
        }
        this.dataset.visible = String(pastHero && !offerVisible);
      }
    }
  );
}
